import * as React from 'react'
import { View } from 'react-native'
import styled from '@theme/styled-components'

import Layout from '@screens/incident/IncidentLayout'
import Activity from '@screens/incident/forms/Activity'

import useReducer from '@store/useReducer'
import * as FamilleStore from '@store/famille/famille'
import * as AuthStore from '@store/auth/auth'

import { AuthScope } from '@api/enum'

interface Props {
  form: IncidentForm
  onForm: (form: Partial<IncidentForm>) => Promise<void>
  onQuit: () => void
  onBack: () => void
  onNext: () => void
  edit?: boolean

  // steps
  steps: IncidentStepScreen[]
  onStep: (step: IncidentStepScreen) => void
}

const stepScreens = ['ACTIVITY', 'ACTIVITY_DETAIL', 'ACTIVITY_DETAIL_SECOND']

const isIncident = (activities: FamilleResponse[], id: string) =>
  !!activities.find((ac) => ac.id === id && !ac.parentIdFamille && ac.libelle.toLowerCase().includes('incident'))

const isInfrastructure = (activities: FamilleResponse[], act: FamilleResponse): boolean => {
  const children = activities.filter((a) => a.parentIdFamille === act.id)

  // last level
  if (children.length === 0) {
    return act.isInfrastructure
  }

  return children.some((sub) => isInfrastructure(activities, sub))
}

const ActivityLayout = ({ form, onForm, onBack, onNext, onQuit, edit, steps, onStep }: Props) => {
  const [step, setStep] = React.useState(0)

  // navigate to next activity
  const [navigateRequired, setNavigateRequired] = React.useState(false)

  const familles = useReducer(FamilleStore.store, (s) => s.activities)
  const acts = React.useMemo(() => familles.filter((f) => f.enabled), [familles])
  const scopes = useReducer(AuthStore.store, (s) => s.scopes)

  const activities = React.useMemo(
    () =>
      acts.filter((act) =>
        form.isInfrastructure === 'PRESTA' ? !act.isInfrastructure : !act.parentIdFamille || isInfrastructure(acts, act)
      ),
    [form.isInfrastructure, acts]
  )

  const selected = form.familles || []

  const datas = [
    activities.filter((a) => !a.parentIdFamille), // first level
    ...selected.map((a) => activities.filter((ac) => ac.parentIdFamille === a)), // sub levels
  ].filter((acts) => acts.length > 0) // filter empty last datas

  React.useEffect(() => {
    // on start if selected has data we go to the selected step (except for edit mode)
    if (edit) {
      setStep(form.famillesMinEditable || 0)
    } else if (form.familles) {
      // can never be under min editable
      setStep(Math.max(form.familles.length - 1, form.famillesMinEditable || 0))
    }
  }, [])

  React.useEffect(() => {
    // register how deep is the lvl
    onForm({ famillesDeepLength: datas.length })
  }, [datas.length])

  React.useEffect(() => {
    if (!form.familles && datas.length > 0) {
      // PRESTATION -> skip first + auto on PRESTA
      if (form.isInfrastructure === 'PRESTA') {
        const presta = datas[0].find((d) => !d.isRemiseEnEtat)

        // preregister "INCIDENT" for prestation
        if (presta) {
          onForm({ familles: [presta.id] })
          setStep(1)
        }
      }

      if (form.isInfrastructure === 'INFRA' && !scopes.includes(AuthScope.DemandesRemiseEnEtatCreation)) {
        const presta = datas[0].find((d) => !d.isRemiseEnEtat)

        if (presta) {
          onForm({ familles: [presta.id] })
          setStep(1)
        }
      }
    }
  }, [form.familles, form.isInfrastructure, datas])

  React.useEffect(() => {
    if (navigateRequired) {
      // on navigation asked, go to next
      // action divided to get an updated workflow before navigate
      setNavigateRequired(false)
      onActivityNext()
    }
  }, [navigateRequired])

  const onActivityNext = () => {
    if (step === datas.length - 1) {
      onNext()
    } else {
      setStep(step + 1)
    }
  }

  const onActivityBack = () => {
    if (step > (form.famillesMinEditable || 0)) {
      setStep(step - 1)
    } else {
      onBack()
    }
  }

  const onItemSelected = (id: string, idx: number) => {
    if (idx === 0) {
      const isRemiseEnEtat = !isIncident(activities, id)

      onForm({ isRemiseEnEtat })

      if (!form.edlId) {
        onForm({ edl: isRemiseEnEtat })
      }
    }

    if (!form.familles || idx === 0) {
      onForm({ familles: [id], completed: false })
    } else if (id && !form.familles.includes(id)) {
      onForm({ familles: [...(form.familles || []).slice(0, idx), id], completed: false })
    }

    setNavigateRequired(true)
  }

  const data = datas[step] || []

  const validate = (f: IncidentForm, data: FamilleResponse[]) => {
    return !!data.find((d) => f.familles && f.familles.includes(d.id))
  }

  return (
    <ItemContainer>
      <Layout
        steps={steps}
        step={step === 0 ? 'ACTIVITY' : 'ACTIVITY_DETAIL'}
        onStep={onStep}
        titleKey={stepScreens[step]}
        form={form}
        onBack={onActivityBack}
        onNext={onActivityNext}
        onQuit={onQuit}
        validation={(f) => validate(f, data)}
        data={data}>
        <Activity
          key={step}
          data={data}
          onItemSelected={(id) => onItemSelected(id, step)}
          selected={form.familles && form.familles[step]}
        />
      </Layout>
    </ItemContainer>
  )
}

export default ActivityLayout

const ItemContainer = styled(View)`
  flex: 1;
`
